import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { SiteHeader, SiteFooter, AGENCY_CONTACT } from '../../components/website/SharedLayout';
import {
  ArrowLeft, ArrowRight, Play, Pause, Volume2, VolumeX, ExternalLink, Monitor, Tablet, Smartphone,
  Target, TrendingUp, ChevronRight, ChevronLeft, Star, Quote, X, Maximize2,
  Image as ImageIcon, Film, CheckCircle, Phone, Mail, MessageSquare, Music, Headphones, Send, Loader2,
  Search, Share2, Heart, MessageCircle, Home, User, Bookmark
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

// ============================================
// YOUTUBE VIDEO PLAYER COMPONENT
// ============================================
const YouTubePlayer = ({ videoUrl, className, title = "Vidéo" }) => {
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const watchMatch = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/);
    if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
    if (url.includes('youtube.com/embed/')) return url.split('?')[0];
    return null;
  };
  
  const embedUrl = getYouTubeEmbedUrl(videoUrl);
  if (!embedUrl) return null;
  
  return (
    <div className={cn("relative rounded-2xl overflow-hidden shadow-2xl", className)}>
      <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 rounded-2xl blur opacity-30" />
      <div className="relative bg-slate-900 rounded-2xl overflow-hidden">
        <div className="bg-slate-800 px-4 py-2 flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <div className="flex-1 flex items-center justify-center">
            <Play className="w-4 h-4 text-red-500 mr-2" />
            <span className="text-xs text-slate-400 truncate">{title}</span>
          </div>
        </div>
        <div className="aspect-video">
          <iframe
            src={`${embedUrl}?rel=0&modestbranding=1`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={title}
          />
        </div>
      </div>
    </div>
  );
};

// ============================================
// GRAPHIC DESIGN GALLERY - Masonry Style
// ============================================
// ============================================
// GRAPHIC SHOWCASE - Animated Gallery
// ============================================
const GraphicShowcase = ({ items = [], projectTitle, onSelect }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  // Filter only images
  const images = items.filter(item => item.type === 'image' || !item.type);
  
  // Auto-advance carousel when not hovered
  useEffect(() => {
    if (images.length <= 1 || isHovered) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [images.length, isHovered]);
  
  if (!images || images.length === 0) return null;
  
  const currentImage = images[activeIndex];
  const imageUrl = currentImage?.url?.startsWith('http') 
    ? currentImage.url 
    : `${process.env.REACT_APP_BACKEND_URL}${currentImage?.url}`;
  
  return (
    <section className="py-16 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500/10 rounded-full mb-4">
            <ImageIcon className="w-4 h-4 text-pink-500" />
            <span className="text-pink-500 text-sm font-medium">Design Graphique</span>
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Galerie créative</h2>
          <p className="text-slate-500">{images.length} {images.length > 1 ? 'visuels' : 'visuel'}</p>
        </div>

        {/* Main Gallery */}
        <div 
          className="relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Main Image Display */}
          <div className="relative bg-slate-900 rounded-3xl overflow-hidden shadow-2xl">
            {/* Image Container with aspect ratio */}
            <div className="relative aspect-[16/10] flex items-center justify-center p-8">
              {/* Background blur effect */}
              <div 
                className="absolute inset-0 opacity-30 blur-3xl scale-110"
                style={{
                  backgroundImage: `url(${imageUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              
              {/* Main Image */}
              <img 
                src={imageUrl}
                alt={currentImage?.caption || projectTitle}
                className="relative z-10 max-w-full max-h-full object-contain rounded-lg shadow-2xl cursor-pointer transition-transform duration-500 hover:scale-105"
                onClick={() => onSelect && onSelect(currentImage, activeIndex)}
              />
              
              {/* Image counter badge */}
              {images.length > 1 && (
                <div className="absolute top-4 right-4 z-20 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full">
                  <span className="text-white text-sm font-medium">{activeIndex + 1} / {images.length}</span>
                </div>
              )}
              
              {/* Caption */}
              {currentImage?.caption && (
                <div className="absolute bottom-4 left-4 right-4 z-20">
                  <div className="bg-black/60 backdrop-blur-sm rounded-xl px-4 py-3">
                    <p className="text-white text-center font-medium">{currentImage.caption}</p>
                  </div>
                </div>
              )}
              
              {/* Navigation arrows */}
              {images.length > 1 && (
                <>
                  <button 
                    onClick={() => setActiveIndex((prev) => (prev - 1 + images.length) % images.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all hover:scale-110"
                  >
                    <ArrowLeft className="w-6 h-6 text-white" />
                  </button>
                  <button 
                    onClick={() => setActiveIndex((prev) => (prev + 1) % images.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all hover:scale-110"
                  >
                    <ArrowRight className="w-6 h-6 text-white" />
                  </button>
                </>
              )}
            </div>
          </div>
          
          {/* Thumbnail Strip */}
          {images.length > 1 && (
            <div className="mt-6 flex justify-center gap-3 flex-wrap">
              {images.map((img, idx) => {
                const thumbUrl = img.url?.startsWith('http') 
                  ? img.url 
                  : `${process.env.REACT_APP_BACKEND_URL}${img.url}`;
                return (
                  <button
                    key={idx}
                    onClick={() => setActiveIndex(idx)}
                    className={cn(
                      "relative w-20 h-20 rounded-xl overflow-hidden transition-all duration-300",
                      idx === activeIndex 
                        ? "ring-4 ring-pink-500 ring-offset-2 scale-110 shadow-lg shadow-pink-500/30" 
                        : "opacity-60 hover:opacity-100 hover:scale-105"
                    )}
                  >
                    <img 
                      src={thumbUrl}
                      alt={`Visuel ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {idx === activeIndex && (
                      <div className="absolute inset-0 bg-pink-500/20" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
          
          {/* Progress dots for auto-advance */}
          {images.length > 1 && (
            <div className="mt-6 flex justify-center gap-2">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={cn(
                    "h-1.5 rounded-full transition-all duration-300",
                    idx === activeIndex 
                      ? "w-8 bg-pink-500" 
                      : "w-1.5 bg-slate-300 hover:bg-slate-400"
                  )}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Enlarge button */}
        <div className="text-center mt-8">
          <Button 
            variant="outline"
            size="lg"
            className="rounded-full"
            onClick={() => onSelect && onSelect(currentImage, activeIndex)}
          >
            <Maximize2 className="w-4 h-4 mr-2" />
            Voir en plein écran
          </Button>
        </div>
      </div>
    </section>
  );
};

// ============================================
// AUDIO PLAYER WITH VISUALIZER
// ============================================
const AudioPlayerWithVisualizer = ({ audioUrl, title = "Piste audio", coverImage }) => {
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const analyserRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [audioContext, setAudioContext] = useState(null);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContext) {
        audioContext.close();
      }
    };
  }, [audioContext]);

  const initAudioContext = () => {
    if (!audioContext && audioRef.current) {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.createAnalyser();
      const source = ctx.createMediaElementSource(audioRef.current);
      source.connect(analyser);
      analyser.connect(ctx.destination);
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      setAudioContext(ctx);
    }
  };

  const visualize = () => {
    if (!canvasRef.current || !analyserRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      
      ctx.fillStyle = 'rgba(15, 23, 42, 0.2)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const barWidth = (canvas.width / bufferLength) * 2.5;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height * 0.8;
        
        // Gradient colors based on frequency
        const hue = (i / bufferLength) * 60 + 180; // Cyan to blue
        ctx.fillStyle = `hsla(${hue}, 80%, 60%, 0.9)`;
        
        // Draw bar with rounded top
        ctx.beginPath();
        ctx.roundRect(x, canvas.height - barHeight, barWidth - 2, barHeight, [4, 4, 0, 0]);
        ctx.fill();
        
        // Mirror effect
        ctx.fillStyle = `hsla(${hue}, 80%, 60%, 0.3)`;
        ctx.beginPath();
        ctx.roundRect(x, canvas.height, barWidth - 2, barHeight * 0.3, [0, 0, 4, 4]);
        ctx.fill();
        
        x += barWidth;
      }
    };
    
    draw();
  };

  const togglePlay = () => {
    initAudioContext();
    if (isPlaying) {
      audioRef.current.pause();
      cancelAnimationFrame(animationRef.current);
    } else {
      audioRef.current.play();
      visualize();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = percent * duration;
  };

  if (!audioUrl) return null;

  return (
    <section className="py-16 bg-slate-900">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <Headphones className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Création audio</h2>
            <p className="text-slate-400">Écoutez notre réalisation</p>
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-6 border border-slate-700">
          {/* Cover & Visualizer */}
          <div className="relative aspect-[2/1] mb-6 rounded-2xl overflow-hidden bg-slate-900">
            {coverImage && (
              <img 
                src={coverImage.startsWith('http') ? coverImage : `${process.env.REACT_APP_BACKEND_URL}${coverImage}`}
                alt={title}
                className="absolute inset-0 w-full h-full object-cover opacity-30"
              />
            )}
            <canvas 
              ref={canvasRef} 
              width={800} 
              height={300}
              className="w-full h-full"
            />
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-slate-900/50" />
            
            {/* Play button overlay */}
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center">
                <button
                  onClick={togglePlay}
                  className="w-20 h-20 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30 hover:scale-110 transition-transform"
                >
                  <Play className="w-8 h-8 text-white ml-1" />
                </button>
              </div>
            )}
          </div>

          {/* Track Info */}
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={togglePlay}
              className="w-14 h-14 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0 hover:scale-105 transition-transform"
            >
              {isPlaying ? (
                <Pause className="w-6 h-6 text-white" />
              ) : (
                <Play className="w-6 h-6 text-white ml-0.5" />
              )}
            </button>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-white truncate">{title}</h3>
              <p className="text-slate-400 text-sm">Création audio originale</p>
            </div>
            <div className="flex items-center gap-2">
              <Volume2 className="w-5 h-5 text-slate-400" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => {
                  setVolume(e.target.value);
                  audioRef.current.volume = e.target.value;
                }}
                className="w-20 accent-cyan-500"
              />
            </div>
          </div>

          {/* Progress Bar */}
          <div 
            className="h-2 bg-slate-700 rounded-full cursor-pointer overflow-hidden"
            onClick={handleSeek}
          >
            <div 
              className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all"
              style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-sm text-slate-400">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>

          {/* Hidden Audio Element */}
          <audio
            ref={audioRef}
            src={audioUrl.startsWith('http') ? audioUrl : `${process.env.REACT_APP_BACKEND_URL}${audioUrl}`}
            crossOrigin="anonymous"
            preload="metadata"
            onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
            onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
            onEnded={() => {
              setIsPlaying(false);
              cancelAnimationFrame(animationRef.current);
            }}
            onError={(e) => console.error('Audio error:', e.target.error)}
          />
        </div>
      </div>
    </section>
  );
};

// ============================================
// VINYL TURNTABLE SHOWCASE
// ============================================
const VinylShowcase = ({ audioUrl, title = "Piste audio", coverImage, artistName = "Agence MSC" }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isLoaded, setIsLoaded] = useState(false);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = percent * duration;
    }
  };

  if (!audioUrl) return null;

  const coverUrl = coverImage 
    ? (coverImage.startsWith('http') ? coverImage : `${process.env.REACT_APP_BACKEND_URL}${coverImage}`)
    : null;

  return (
    <section className="py-16 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 rounded-full mb-4">
            <Music className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">Production Audio</span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Création Sonore</h2>
          <p className="text-slate-400">Écoutez notre réalisation audio</p>
        </div>

        {/* Turntable */}
        <div className="relative max-w-2xl mx-auto">
          {/* Turntable Base */}
          <div className="relative bg-gradient-to-b from-[#2a2018] via-[#1a1410] to-[#0d0a08] rounded-3xl p-8 shadow-2xl border border-amber-900/30">
            
            {/* Wood Grain Texture Overlay */}
            <div className="absolute inset-0 rounded-3xl opacity-20 pointer-events-none" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }} />
            
            {/* Turntable Platter Area */}
            <div className="relative flex justify-center">
              {/* Platter */}
              <div className="relative w-[320px] h-[320px] md:w-[380px] md:h-[380px]">
                {/* Platter Base - Metal */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800 shadow-xl" />
                
                {/* Rubber Mat */}
                <div className="absolute inset-2 rounded-full bg-gradient-to-br from-slate-900 to-black" />
                
                {/* Vinyl Record */}
                <div 
                  className={cn(
                    "absolute inset-4 rounded-full shadow-2xl transition-all duration-300",
                    isPlaying ? "animate-spin" : ""
                  )}
                  style={{ 
                    animationDuration: '2s',
                    animationTimingFunction: 'linear',
                    animationIterationCount: 'infinite'
                  }}
                >
                  {/* Vinyl Grooves */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-slate-900 via-black to-slate-900">
                    {/* Groove rings */}
                    {[...Array(8)].map((_, i) => (
                      <div 
                        key={i}
                        className="absolute rounded-full border border-slate-700/30"
                        style={{
                          inset: `${10 + i * 10}px`,
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Vinyl Shine */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none" />
                  
                  {/* Label (Center) */}
                  <div className="absolute inset-[30%] rounded-full overflow-hidden shadow-inner">
                    {coverUrl ? (
                      <img 
                        src={coverUrl}
                        alt={title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-amber-600 via-amber-700 to-amber-900 flex items-center justify-center">
                        <div className="text-center">
                          <Music className="w-8 h-8 text-amber-300 mx-auto mb-1" />
                          <span className="text-amber-200 text-[10px] font-bold tracking-wider">MSC</span>
                        </div>
                      </div>
                    )}
                    {/* Label shine */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
                  </div>
                  
                  {/* Center Spindle Hole */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-slate-800 shadow-inner border border-slate-600" />
                </div>
                
                {/* Center Spindle */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gradient-to-b from-slate-400 to-slate-600 shadow-lg z-10" />
              </div>
              
              {/* Tonearm */}
              <div className="absolute right-4 top-4 md:right-8 md:top-8">
                {/* Tonearm Base */}
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-b from-slate-500 to-slate-700 shadow-lg" />
                  {/* Tonearm Arm */}
                  <div 
                    className={cn(
                      "absolute top-6 left-6 w-2 h-40 bg-gradient-to-b from-slate-400 to-slate-600 rounded-full origin-top transition-transform duration-500 shadow-md",
                      isPlaying ? "rotate-[25deg]" : "rotate-[45deg]"
                    )}
                  >
                    {/* Headshell */}
                    <div className="absolute -bottom-2 -left-2 w-6 h-8 bg-gradient-to-b from-slate-500 to-slate-700 rounded-sm transform rotate-[-10deg]">
                      {/* Cartridge */}
                      <div className="absolute bottom-0 left-1 w-4 h-3 bg-slate-800 rounded-sm" />
                      {/* Stylus */}
                      <div className="absolute -bottom-1 left-2 w-0.5 h-2 bg-slate-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Controls Section */}
            <div className="mt-8 pt-6 border-t border-amber-900/30">
              {/* Track Info */}
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={togglePlay}
                  className="w-16 h-16 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 hover:scale-105 transition-transform shadow-lg shadow-amber-500/30"
                >
                  {isPlaying ? (
                    <Pause className="w-7 h-7 text-white" />
                  ) : (
                    <Play className="w-7 h-7 text-white ml-1" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-white truncate">{title}</h3>
                  <p className="text-amber-400 text-sm">{artistName}</p>
                </div>
                {/* Volume Control - Vintage Knob Style */}
                <div className="flex flex-col items-center gap-1">
                  <div className="relative w-12 h-12 rounded-full bg-gradient-to-b from-slate-600 to-slate-800 shadow-lg flex items-center justify-center">
                    <div 
                      className="w-8 h-8 rounded-full bg-gradient-to-b from-amber-500 to-amber-700 shadow-inner flex items-center justify-center"
                      style={{ transform: `rotate(${(volume - 0.5) * 270}deg)` }}
                    >
                      <div className="w-0.5 h-3 bg-white/80 rounded-full transform -translate-y-1" />
                    </div>
                  </div>
                  <span className="text-slate-500 text-xs">Vol</span>
                </div>
              </div>

              {/* Progress Bar - Vintage Style */}
              <div className="space-y-2">
                <div 
                  className="h-3 bg-slate-800 rounded-full cursor-pointer overflow-hidden border border-slate-700"
                  onClick={handleSeek}
                >
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 rounded-full transition-all relative"
                    style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                  >
                    {/* Needle indicator */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow-lg border-2 border-amber-500" />
                  </div>
                </div>
                <div className="flex justify-between text-sm text-slate-500 font-mono">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
              
              {/* Volume Slider (Hidden, controlled by knob appearance) */}
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => {
                  setVolume(parseFloat(e.target.value));
                  if (audioRef.current) audioRef.current.volume = parseFloat(e.target.value);
                }}
                className="sr-only"
              />
            </div>
            
            {/* Brand Badge */}
            <div className="absolute bottom-4 right-4">
              <div className="px-3 py-1 bg-amber-900/50 rounded-full border border-amber-700/50">
                <span className="text-amber-400 text-xs font-bold tracking-widest">MSC AUDIO</span>
              </div>
            </div>
          </div>
          
          {/* Reflection */}
          <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-[80%] h-20 bg-gradient-to-b from-white/5 to-transparent rounded-full blur-xl" />
        </div>

        {/* Hidden Audio Element */}
        <audio
          ref={audioRef}
          src={audioUrl.startsWith('http') ? audioUrl : `${process.env.REACT_APP_BACKEND_URL}${audioUrl}`}
          preload="metadata"
          onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
          onLoadedMetadata={() => {
            setDuration(audioRef.current?.duration || 0);
            setIsLoaded(true);
          }}
          onEnded={() => setIsPlaying(false)}
          onError={(e) => console.error('Audio error:', e.target.error)}
        />
      </div>
    </section>
  );
};

// ============================================
// VIDEO SERIES GALLERY (Multiple YouTube Videos)
// ============================================
const VideoSeriesGallery = ({ videos = [], projectTitle }) => {
  const [activeVideo, setActiveVideo] = useState(0);
  
  // Filter YouTube videos
  const youtubeVideos = videos.filter(v => 
    v.type === 'video' || v.type === 'youtube' || 
    (v.url && (v.url.includes('youtube') || v.url.includes('youtu.be')))
  );
  
  if (youtubeVideos.length === 0) return null;

  return (
    <section className="py-16 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
            <Film className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">
              {youtubeVideos.length > 1 ? 'Série vidéo' : 'Vidéo'}
            </h2>
            <p className="text-slate-400">
              {youtubeVideos.length} vidéo{youtubeVideos.length > 1 ? 's' : ''} à découvrir
            </p>
          </div>
        </div>

        {/* Main Video Player */}
        <div className="mb-6">
          <YouTubePlayer 
            videoUrl={youtubeVideos[activeVideo]?.url} 
            title={youtubeVideos[activeVideo]?.caption || `${projectTitle} - Épisode ${activeVideo + 1}`}
          />
        </div>

        {/* Video Thumbnails (if multiple) */}
        {youtubeVideos.length > 1 && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {youtubeVideos.map((video, idx) => {
              const videoId = video.url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)?.[1];
              const thumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
              
              return (
                <button
                  key={idx}
                  onClick={() => setActiveVideo(idx)}
                  className={cn(
                    "relative aspect-video rounded-xl overflow-hidden group transition-all",
                    activeVideo === idx 
                      ? "ring-2 ring-purple-500 ring-offset-2 ring-offset-slate-900" 
                      : "opacity-70 hover:opacity-100"
                  )}
                >
                  {thumbnail && (
                    <img 
                      src={thumbnail} 
                      alt={video.caption || `Épisode ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center transition-transform",
                      activeVideo === idx 
                        ? "bg-purple-500 scale-110" 
                        : "bg-white/90 group-hover:scale-110"
                    )}>
                      <Play className={cn(
                        "w-4 h-4 ml-0.5",
                        activeVideo === idx ? "text-white" : "text-slate-900"
                      )} />
                    </div>
                  </div>
                  <Badge className={cn(
                    "absolute bottom-2 left-2 text-xs",
                    activeVideo === idx ? "bg-purple-500" : "bg-black/60"
                  )}>
                    Ép. {idx + 1}
                  </Badge>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

// ============================================
// WEBSITE MULTI-SCREEN SHOWCASE
// ============================================
const WebsiteShowcase = ({ websiteUrl, projectTitle, previewImage, screenshotDesktop, screenshotTablet, screenshotMobile }) => {
  const hasResponsiveScreenshots = screenshotDesktop || screenshotTablet || screenshotMobile;
  const hasWebsiteUrl = websiteUrl && websiteUrl !== '#' && websiteUrl.startsWith('http');
  
  if (!hasWebsiteUrl && !hasResponsiveScreenshots) return null;

  // Render device content (screenshot image or iframe fallback)
  const renderDeviceContent = (screenshot, alt, deviceType) => {
    // Priority 1: Show screenshot if available
    if (screenshot) {
      return (
        <img 
          src={screenshot} 
          alt={alt}
          className="w-full h-full object-cover object-top"
        />
      );
    }
    
    // Priority 2: Show iframe with website URL if available
    if (hasWebsiteUrl) {
      const scaleMap = { desktop: 0.4, tablet: 0.35, mobile: 0.3 };
      const scale = scaleMap[deviceType] || 0.4;
      
      return (
        <div className="w-full h-full overflow-hidden relative bg-white">
          <iframe
            src={websiteUrl}
            title={alt}
            className="absolute top-0 left-0 border-0"
            style={{
              width: `${100 / scale}%`,
              height: `${100 / scale}%`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              pointerEvents: 'none'
            }}
            loading="lazy"
          />
        </div>
      );
    }
    
    // Priority 3: Show placeholder
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-800/50">
        <div className="text-center text-slate-500">
          <Monitor className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-xs">Aperçu non disponible</p>
        </div>
      </div>
    );
  };

  return (
    <section className="py-16 bg-gradient-to-b from-slate-900 to-slate-800 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 rounded-full mb-4">
            <Monitor className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm font-medium">Design Responsive</span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Aperçu multi-écrans</h2>
          <p className="text-slate-400">Une expérience optimisée sur tous les appareils</p>
          {hasWebsiteUrl && (
            <p className="text-cyan-400 text-sm mt-2 flex items-center justify-center gap-2">
              <ExternalLink className="w-3 h-3" />
              <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
                {websiteUrl}
              </a>
            </p>
          )}
        </div>

        {/* Devices Container */}
        <div className="flex items-end justify-center gap-4 md:gap-6 lg:gap-10">
          {/* Desktop */}
          <div className="hidden lg:block" style={{ width: '55%', maxWidth: '700px' }}>
            <div className="bg-slate-700 rounded-t-lg p-2 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
              </div>
              <div className="flex-1 bg-slate-600 rounded px-3 py-1 text-xs text-slate-400 truncate">
                {websiteUrl || projectTitle}
              </div>
            </div>
            <div className="bg-slate-800 border-4 border-slate-700 rounded-b-lg overflow-hidden" style={{ height: '400px' }}>
              {renderDeviceContent(screenshotDesktop, `${projectTitle} - Desktop`, 'desktop')}
            </div>
            <div className="flex justify-center mt-2">
              <div className="w-20 h-1 bg-slate-600 rounded-full" />
            </div>
            <p className="text-center text-slate-500 text-sm mt-2 flex items-center justify-center gap-2">
              <Monitor className="w-4 h-4" /> Desktop
            </p>
          </div>

          {/* Tablet */}
          <div className="hidden md:block" style={{ width: '25%', maxWidth: '280px' }}>
            <div className="bg-slate-700 rounded-t-2xl p-2 flex justify-center">
              <div className="w-16 h-1 bg-slate-600 rounded-full" />
            </div>
            <div className="bg-slate-800 border-4 border-slate-700 overflow-hidden" style={{ height: '350px' }}>
              {renderDeviceContent(screenshotTablet, `${projectTitle} - Tablet`, 'tablet')}
            </div>
            <div className="bg-slate-700 rounded-b-2xl p-3 flex justify-center">
              <div className="w-8 h-8 rounded-full border-2 border-slate-600" />
            </div>
            <p className="text-center text-slate-500 text-sm mt-2 flex items-center justify-center gap-2">
              <Tablet className="w-4 h-4" /> Tablette
            </p>
          </div>

          {/* Mobile - iPhone Style */}
          <div style={{ width: '18%', maxWidth: '140px', minWidth: '120px' }}>
            {/* iPhone Frame */}
            <div className="relative mx-auto">
              {/* Phone Body - Titanium/Space Black style */}
              <div className="bg-gradient-to-b from-slate-700 via-slate-800 to-slate-900 rounded-[2rem] p-[2px] shadow-2xl">
                {/* Inner bezel */}
                <div className="bg-black rounded-[1.9rem] p-[1.5px]">
                  {/* Screen area */}
                  <div className="relative bg-black rounded-[1.8rem] overflow-hidden" style={{ height: '280px' }}>
                    {/* Dynamic Island */}
                    <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-20">
                      <div className="bg-black w-[60px] h-[18px] rounded-full flex items-center justify-center gap-2 shadow-inner">
                        <div className="w-[6px] h-[6px] rounded-full bg-slate-900 ring-1 ring-slate-700" />
                        <div className="w-[5px] h-[5px] rounded-full bg-slate-800" />
                      </div>
                    </div>
                    
                    {/* Screen Content */}
                    <div className="w-full h-full">
                      {renderDeviceContent(screenshotMobile, `${projectTitle} - Mobile`, 'mobile')}
                    </div>
                    
                    {/* Home Indicator */}
                    <div className="absolute bottom-1.5 left-1/2 transform -translate-x-1/2 z-20">
                      <div className="w-[70px] h-[3px] bg-white/30 rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Side Buttons */}
              {/* Volume Up */}
              <div className="absolute left-[-1.5px] top-[60px] w-[2px] h-[25px] bg-slate-600 rounded-l-sm" />
              {/* Volume Down */}
              <div className="absolute left-[-1.5px] top-[92px] w-[2px] h-[25px] bg-slate-600 rounded-l-sm" />
              {/* Silent Switch */}
              <div className="absolute left-[-1.5px] top-[42px] w-[2px] h-[12px] bg-slate-600 rounded-l-sm" />
              {/* Power Button */}
              <div className="absolute right-[-1.5px] top-[70px] w-[2px] h-[35px] bg-slate-600 rounded-r-sm" />
            </div>
            
            <p className="text-center text-slate-500 text-sm mt-4 flex items-center justify-center gap-2">
              <Smartphone className="w-4 h-4" /> Mobile
            </p>
          </div>
        </div>

        {/* Visit Website Button */}
        {hasWebsiteUrl && (
          <div className="flex justify-center mt-12">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 rounded-full px-8 shadow-lg shadow-cyan-500/30"
              onClick={() => window.open(websiteUrl, '_blank')}
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Voir le site en ligne
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

// ============================================
// VIDEO SHOWCASE COMPONENT (TV + TikTok Phone)
// ============================================
const VideoShowcase = ({ youtubeUrl, projectTitle, isPortrait = false, videos = [] }) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay
  const iframeRef = useRef(null);
  
  // Extract YouTube video ID
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };
  
  // Extract TikTok video ID
  const getTikTokVideoId = (url) => {
    if (!url) return null;
    // Match patterns like: tiktok.com/@user/video/1234567890 or vm.tiktok.com/ABC123
    const match = url.match(/tiktok\.com\/@[\w.-]+\/video\/(\d+)|vm\.tiktok\.com\/(\w+)/);
    return match ? (match[1] || match[2]) : null;
  };
  
  // Detect video platform
  const getVideoPlatform = (url) => {
    if (!url) return null;
    if (getYouTubeVideoId(url)) return 'youtube';
    if (getTikTokVideoId(url) || url.includes('tiktok.com')) return 'tiktok';
    return null;
  };
  
  // Build video list from single URL or array
  const videoList = videos.length > 0 
    ? videos.filter(v => getYouTubeVideoId(v.url) || getTikTokVideoId(v.url) || v.url?.includes('tiktok.com'))
    : youtubeUrl ? [{ url: youtubeUrl, caption: projectTitle }] : [];
  
  if (videoList.length === 0) return null;
  
  const currentVideo = videoList[currentVideoIndex];
  const platform = getVideoPlatform(currentVideo.url);
  const youtubeVideoId = getYouTubeVideoId(currentVideo.url);
  const tiktokVideoId = getTikTokVideoId(currentVideo.url);
  
  // Build embed URL based on platform and mute state
  const getEmbedUrl = (muted) => {
    if (platform === 'youtube' && youtubeVideoId) {
      return `https://www.youtube.com/embed/${youtubeVideoId}?rel=0&modestbranding=1&autoplay=1&mute=${muted ? 1 : 0}&loop=1&playlist=${youtubeVideoId}&enablejsapi=1`;
    }
    if (platform === 'tiktok') {
      // TikTok embed URL
      return `https://www.tiktok.com/embed/v2/${tiktokVideoId}?autoplay=1&mute=${muted ? 1 : 0}`;
    }
    return null;
  };
  
  const embedUrl = getEmbedUrl(isMuted);
  const thumbnailUrl = youtubeVideoId 
    ? `https://img.youtube.com/vi/${youtubeVideoId}/maxresdefault.jpg`
    : null;
  
  const hasMultipleVideos = videoList.length > 1;
  
  // Toggle mute/unmute
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  return (
    <section className="py-16 bg-gradient-to-b from-slate-900 to-slate-800 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full mb-4">
            <Film className="w-4 h-4 text-purple-400" />
            <span className="text-purple-400 text-sm font-medium">Production Vidéo</span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            {hasMultipleVideos ? 'Série Vidéo' : 'Aperçu de la réalisation'}
          </h2>
          <p className="text-slate-400">
            {hasMultipleVideos ? `${videoList.length} épisodes` : 'Visionnez notre création vidéo'}
          </p>
        </div>

        {/* Video Display */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12">
          
          {/* TV Screen - Landscape Video */}
          {!isPortrait && (
            <div className="w-full max-w-3xl">
              {/* TV Frame */}
              <div className="relative">
                {/* TV Body */}
                <div className="bg-slate-800 rounded-xl p-4 shadow-2xl">
                  {/* Screen Bezel */}
                  <div className="bg-black rounded-lg overflow-hidden relative">
                    {/* Screen Content */}
                    <div className="aspect-video">
                      <iframe
                        ref={iframeRef}
                        src={embedUrl}
                        title={currentVideo.caption || projectTitle}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    {/* Screen Reflection */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />
                    
                    {/* Sound Toggle Button - Positioned on TV screen */}
                    <button
                      onClick={toggleMute}
                      className="absolute bottom-4 right-4 z-10 w-12 h-12 rounded-full bg-black/70 hover:bg-black/90 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all duration-300 group hover:scale-110"
                      title={isMuted ? "Activer le son" : "Couper le son"}
                    >
                      {isMuted ? (
                        <VolumeX className="w-5 h-5 text-white group-hover:text-red-400 transition-colors" />
                      ) : (
                        <Volume2 className="w-5 h-5 text-white group-hover:text-green-400 transition-colors" />
                      )}
                    </button>
                  </div>
                  {/* TV Bottom - Brand & Caption */}
                  <div className="flex items-center justify-between mt-3 px-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-slate-500 text-xs font-medium tracking-wider">MSC TV</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {currentVideo.caption && (
                        <span className="text-slate-400 text-sm truncate max-w-[200px]">{currentVideo.caption}</span>
                      )}
                      {/* Sound indicator */}
                      <div className={cn(
                        "flex items-center gap-1 px-2 py-1 rounded-full text-xs",
                        isMuted ? "bg-red-500/20 text-red-400" : "bg-green-500/20 text-green-400"
                      )}>
                        {isMuted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                        <span>{isMuted ? "Muet" : "Son"}</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* TV Stand */}
                <div className="flex justify-center mt-3">
                  <div className="w-32 h-4 bg-slate-700 rounded-t-lg" />
                </div>
                <div className="flex justify-center">
                  <div className="w-48 h-2 bg-slate-600 rounded-b-lg shadow-lg" />
                </div>
              </div>
              
              {/* Video Carousel Navigation */}
              {hasMultipleVideos && (
                <div className="mt-8">
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <button 
                      onClick={() => setCurrentVideoIndex(prev => prev > 0 ? prev - 1 : videoList.length - 1)}
                      className="w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 text-white" />
                    </button>
                    <span className="text-white font-medium">
                      {currentVideoIndex + 1} / {videoList.length}
                    </span>
                    <button 
                      onClick={() => setCurrentVideoIndex(prev => prev < videoList.length - 1 ? prev + 1 : 0)}
                      className="w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center transition-colors"
                    >
                      <ChevronRight className="w-5 h-5 text-white" />
                    </button>
                  </div>
                  {/* Thumbnails */}
                  <div className="flex justify-center gap-2 overflow-x-auto pb-2">
                    {videoList.map((video, index) => {
                      const thumbId = getYouTubeVideoId(video.url);
                      return (
                        <button
                          key={index}
                          onClick={() => setCurrentVideoIndex(index)}
                          className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                            index === currentVideoIndex 
                              ? 'border-purple-500 scale-105' 
                              : 'border-transparent opacity-60 hover:opacity-100'
                          }`}
                        >
                          <img 
                            src={`https://img.youtube.com/vi/${thumbId}/mqdefault.jpg`}
                            alt={video.caption || `Vidéo ${index + 1}`}
                            className="w-24 h-14 object-cover"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <p className="text-center text-slate-500 text-sm mt-4 flex items-center justify-center gap-2">
                <Monitor className="w-4 h-4" /> Format paysage
              </p>
            </div>
          )}

          {/* iPhone 15 Pro Max - Portrait Video Only */}
          {isPortrait && (
            <div className="w-full max-w-sm mx-auto">
              {/* iPhone 15 Pro Max Frame */}
              <div className="relative mx-auto" style={{ width: '300px' }}>
                {/* Phone Body - Titanium style */}
                <div className="bg-gradient-to-b from-[#3a3a3c] via-[#2c2c2e] to-[#1c1c1e] rounded-[55px] p-[4px] shadow-2xl">
                  {/* Inner frame */}
                  <div className="bg-gradient-to-b from-[#1c1c1e] to-black rounded-[52px] p-[3px]">
                    {/* Screen bezel */}
                    <div className="bg-black rounded-[49px] overflow-hidden relative" style={{ height: '650px' }}>
                      
                      {/* Dynamic Island */}
                      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-30">
                        <div className="bg-black w-[120px] h-[35px] rounded-full flex items-center justify-between px-4 shadow-lg border border-slate-800/50">
                          <div className="w-[12px] h-[12px] rounded-full bg-[#1c1c1e] ring-1 ring-slate-700/50" />
                          <div className="w-[10px] h-[10px] rounded-full bg-[#262626]" />
                        </div>
                      </div>
                      
                      {/* Video Content - YouTube Embed with autoplay mute */}
                      <div className="w-full h-full">
                        <iframe
                          src={embedUrl}
                          title={projectTitle}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                      
                      {/* TikTok/Reels UI Overlay */}
                      <div className="absolute inset-0 pointer-events-none z-20">
                        {/* Top gradient for visibility */}
                        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/60 to-transparent" />
                        
                        {/* Top Bar */}
                        <div className="absolute top-16 left-0 right-0 px-4 flex items-center justify-center gap-6">
                          <span className="text-white/60 text-sm font-medium">Abonnements</span>
                          <span className="text-white text-sm font-semibold border-b-2 border-white pb-1">Pour toi</span>
                          <Search className="w-5 h-5 text-white/80" />
                        </div>
                        
                        {/* Right Side Actions */}
                        <div className="absolute right-3 bottom-32 flex flex-col items-center gap-5">
                          {/* Profile */}
                          <div className="relative">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 p-[2px]">
                              <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center">
                                <span className="text-white text-xs font-bold">MSC</span>
                              </div>
                            </div>
                            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center border-2 border-black">
                              <span className="text-white text-sm font-bold">+</span>
                            </div>
                          </div>
                          {/* Like */}
                          <div className="flex flex-col items-center">
                            <div className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                              <Heart className="w-6 h-6 text-white fill-red-500" />
                            </div>
                            <span className="text-white text-xs mt-1 font-medium">24.5K</span>
                          </div>
                          {/* Comment */}
                          <div className="flex flex-col items-center">
                            <div className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                              <MessageCircle className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-white text-xs mt-1 font-medium">1,247</span>
                          </div>
                          {/* Bookmark */}
                          <div className="flex flex-col items-center">
                            <div className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                              <Bookmark className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-white text-xs mt-1 font-medium">8,432</span>
                          </div>
                          {/* Share */}
                          <div className="flex flex-col items-center">
                            <div className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                              <Share2 className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-white text-xs mt-1 font-medium">Partager</span>
                          </div>
                          {/* Sound Toggle - Interactive */}
                          <div className="flex flex-col items-center pointer-events-auto">
                            <button
                              onClick={toggleMute}
                              className={cn(
                                "w-11 h-11 rounded-full backdrop-blur-sm flex items-center justify-center transition-all duration-300 hover:scale-110",
                                isMuted 
                                  ? "bg-red-500/30 border-2 border-red-500/50" 
                                  : "bg-green-500/30 border-2 border-green-500/50"
                              )}
                              title={isMuted ? "Activer le son" : "Couper le son"}
                            >
                              {isMuted ? (
                                <VolumeX className="w-6 h-6 text-white" />
                              ) : (
                                <Volume2 className="w-6 h-6 text-white" />
                              )}
                            </button>
                            <span className="text-white text-xs mt-1 font-medium">{isMuted ? "Muet" : "Son"}</span>
                          </div>
                          {/* Music Disc */}
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center animate-spin border-2 border-slate-600" style={{ animationDuration: '3s' }}>
                            <div className="w-5 h-5 rounded-full bg-slate-400" />
                          </div>
                        </div>
                        
                        {/* Bottom gradient for visibility */}
                        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/80 to-transparent" />
                        
                        {/* Bottom Info */}
                        <div className="absolute left-4 right-20 bottom-24">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="text-white font-bold text-base">@agence_msc</p>
                            <button className="px-3 py-1 border border-white/80 rounded text-white text-xs font-medium">
                              Suivre
                            </button>
                          </div>
                          <p className="text-white text-sm leading-relaxed line-clamp-2">
                            {currentVideo.caption || projectTitle} ✨ #AgenceMSC #Production #Video
                          </p>
                          {/* Music */}
                          <div className="flex items-center gap-2 mt-3">
                            <Music className="w-4 h-4 text-white" />
                            <div className="overflow-hidden">
                              <span className="text-white text-sm whitespace-nowrap animate-marquee">
                                🎵 Son original - Agence MSC • Musique tendance
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Bottom Nav Bar */}
                        <div className="absolute bottom-0 left-0 right-0 h-20 bg-black flex items-center justify-around px-6 pt-2">
                          <div className="flex flex-col items-center gap-1">
                            <Home className="w-6 h-6 text-white" />
                            <span className="text-white text-[10px]">Accueil</span>
                          </div>
                          <div className="flex flex-col items-center gap-1">
                            <Search className="w-6 h-6 text-white/50" />
                            <span className="text-white/50 text-[10px]">Découvrir</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <div className="w-12 h-8 bg-white rounded-lg flex items-center justify-center -mt-1">
                              <span className="text-black text-2xl font-light">+</span>
                            </div>
                          </div>
                          <div className="flex flex-col items-center gap-1">
                            <MessageCircle className="w-6 h-6 text-white/50" />
                            <span className="text-white/50 text-[10px]">Boîte</span>
                          </div>
                          <div className="flex flex-col items-center gap-1">
                            <User className="w-6 h-6 text-white/50" />
                            <span className="text-white/50 text-[10px]">Profil</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Home Indicator */}
                      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-30">
                        <div className="w-[140px] h-[5px] bg-white/80 rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Side Buttons - iPhone 15 Pro Max style */}
                {/* Action Button (top left) */}
                <div className="absolute left-[-3px] top-[100px] w-[4px] h-[30px] bg-[#3a3a3c] rounded-l-sm shadow-md" />
                {/* Volume Up */}
                <div className="absolute left-[-3px] top-[150px] w-[4px] h-[60px] bg-[#3a3a3c] rounded-l-sm shadow-md" />
                {/* Volume Down */}
                <div className="absolute left-[-3px] top-[220px] w-[4px] h-[60px] bg-[#3a3a3c] rounded-l-sm shadow-md" />
                {/* Power Button */}
                <div className="absolute right-[-3px] top-[180px] w-[4px] h-[80px] bg-[#3a3a3c] rounded-r-sm shadow-md" />
              </div>
              
              {/* Video Carousel Navigation for Portrait */}
              {hasMultipleVideos && (
                <div className="mt-8">
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <button 
                      onClick={() => setCurrentVideoIndex(prev => prev > 0 ? prev - 1 : videoList.length - 1)}
                      className="w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 text-white" />
                    </button>
                    <span className="text-white font-medium">
                      {currentVideoIndex + 1} / {videoList.length}
                    </span>
                    <button 
                      onClick={() => setCurrentVideoIndex(prev => prev < videoList.length - 1 ? prev + 1 : 0)}
                      className="w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center transition-colors"
                    >
                      <ChevronRight className="w-5 h-5 text-white" />
                    </button>
                  </div>
                  {/* Portrait Thumbnails - Vertical format */}
                  <div className="flex justify-center gap-3 overflow-x-auto pb-2 px-4">
                    {videoList.map((video, index) => {
                      const thumbId = getYouTubeVideoId(video.url);
                      const isTikTok = getVideoPlatform(video.url) === 'tiktok';
                      return (
                        <button
                          key={index}
                          onClick={() => setCurrentVideoIndex(index)}
                          className={`flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all ${
                            index === currentVideoIndex 
                              ? 'border-pink-500 scale-105 shadow-lg shadow-pink-500/30' 
                              : 'border-transparent opacity-60 hover:opacity-100'
                          }`}
                        >
                          {thumbId ? (
                            <img 
                              src={`https://img.youtube.com/vi/${thumbId}/mqdefault.jpg`}
                              alt={video.caption || `Vidéo ${index + 1}`}
                              className="w-16 h-28 object-cover"
                            />
                          ) : (
                            <div className="w-16 h-28 bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                              {isTikTok ? (
                                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                                </svg>
                              ) : (
                                <Play className="w-6 h-6 text-white" />
                              )}
                            </div>
                          )}
                          {/* Episode indicator */}
                          <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                            <span className="text-[10px] text-white bg-black/60 px-1.5 py-0.5 rounded-full">
                              {index + 1}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {/* Caption */}
                  {currentVideo.caption && (
                    <p className="text-center text-slate-400 text-sm mt-3">
                      {currentVideo.caption}
                    </p>
                  )}
                </div>
              )}
              
              <p className="text-center text-slate-400 text-sm mt-6 flex items-center justify-center gap-2">
                <Smartphone className="w-4 h-4" /> Format Reels / TikTok
              </p>
            </div>
          )}
        </div>

        {/* Watch Video Button - YouTube or TikTok */}
        <div className="flex justify-center mt-12 gap-4">
          {platform === 'youtube' && (
            <Button 
              size="lg"
              className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-full px-8 shadow-lg shadow-red-500/30"
              onClick={() => window.open(currentVideo.url, '_blank')}
            >
              <Play className="w-5 h-5 mr-2" />
              Voir sur YouTube
            </Button>
          )}
          {platform === 'tiktok' && (
            <Button 
              size="lg"
              className="bg-gradient-to-r from-[#00f2ea] via-[#ff0050] to-[#00f2ea] hover:opacity-90 rounded-full px-8 shadow-lg text-white"
              onClick={() => window.open(currentVideo.url, '_blank')}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
              Voir sur TikTok
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

// ============================================
// STANDARD GALLERY (Fallback)
// ============================================
const MediaGallery = ({ items = [], onSelect }) => {
  if (!items || items.length === 0) return null;
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {items.map((item, idx) => (
        <div 
          key={idx}
          onClick={() => onSelect(item, idx)}
          className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer bg-slate-100"
        >
          {item.type === 'video' || item.type === 'youtube' ? (
            <>
              <img 
                src={item.thumbnail || `https://img.youtube.com/vi/${item.url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)?.[1]}/hqdefault.jpg`}
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="w-6 h-6 text-slate-900 ml-1" />
                </div>
              </div>
              <Badge className="absolute top-2 right-2 bg-red-500">
                <Film className="w-3 h-3 mr-1" /> Vidéo
              </Badge>
            </>
          ) : (
            <>
              <img 
                src={item.url?.startsWith('http') ? item.url : `${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                alt={item.caption || ''}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <ImageIcon className="w-8 h-8 text-white" />
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================
// LIGHTBOX COMPONENT
// ============================================
const Lightbox = ({ items, currentIndex, onClose, onPrev, onNext }) => {
  const item = items[currentIndex];
  if (!item) return null;
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl bg-black/95 border-0 p-0">
        <button 
          onClick={onClose}
          className="absolute right-4 top-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X className="w-6 h-6 text-white" />
        </button>
        
        <div className="relative">
          {item.type === 'video' || item.type === 'youtube' ? (
            <YouTubePlayer videoUrl={item.url} className="rounded-none" />
          ) : (
            <img 
              src={item.url?.startsWith('http') ? item.url : `${process.env.REACT_APP_BACKEND_URL}${item.url}`}
              alt=""
              className="w-full max-h-[80vh] object-contain"
            />
          )}
          
          {items.length > 1 && (
            <>
              <button 
                onClick={onPrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button 
                onClick={onNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </>
          )}
        </div>
        
        <div className="p-4 text-center">
          {item.caption && <p className="text-white mb-2">{item.caption}</p>}
          <p className="text-slate-400 text-sm">{currentIndex + 1} / {items.length}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// CLIENT TESTIMONIAL COMPONENT
// ============================================
const ClientTestimonial = ({ testimonial, showVideo = true, showText = true }) => {
  if (!testimonial) return null;
  
  const content = testimonial.content || testimonial.text || '';
  const author = testimonial.author || testimonial.author_name || '';
  const role = testimonial.role || testimonial.author_role || '';
  const photo = testimonial.photo || testimonial.author_photo || '';
  const rating = testimonial.rating || 5;
  const videoUrl = testimonial.video_url || '';
  
  return (
    <div className="bg-gradient-to-br from-slate-50 to-white rounded-3xl p-8 lg:p-12 border border-slate-100">
      <div className="flex items-center gap-2 mb-6">
        <MessageSquare className="w-5 h-5 text-[#00d4aa]" />
        <h3 className="text-xl font-bold text-slate-900">Témoignage client</h3>
      </div>
      
      <div className="grid lg:grid-cols-2 gap-8 items-center">
        {showText && content && (
          <div>
            <Quote className="w-10 h-10 text-[#00d4aa]/30 mb-4" />
            <blockquote className="text-lg text-slate-700 italic mb-6 leading-relaxed">
              &ldquo;{content}&rdquo;
            </blockquote>
            
            <div className="flex items-center gap-4">
              {photo && (
                <img 
                  src={photo?.startsWith('http') ? photo : `${process.env.REACT_APP_BACKEND_URL}${photo}`}
                  alt={author}
                  className="w-14 h-14 rounded-full object-cover"
                />
              )}
              <div>
                <p className="font-semibold text-slate-900">{author}</p>
                <p className="text-sm text-slate-500">{role}</p>
              </div>
              {rating && (
                <div className="flex gap-0.5 ml-auto">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={cn("w-4 h-4", i < rating ? "fill-amber-400 text-amber-400" : "text-slate-200")} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        {showVideo && videoUrl && (
          <YouTubePlayer videoUrl={videoUrl} title="Témoignage vidéo" />
        )}
      </div>
    </div>
  );
};

// ============================================
// MAIN CASE STUDY PAGE COMPONENT
// ============================================
const CaseStudyPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  
  // Site settings for lead pages configuration
  const [siteSettings, setSiteSettings] = useState(null);
  
  // Quote modal state
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    service: '',
    project_ref: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // External lead management endpoint
  const LEAD_API_ENDPOINT = 'https://portfolio-preview-104.preview.emergentagent.com/api/leads/embed';

  // Function to handle quote action - redirect to external page or open modal
  const handleQuoteAction = () => {
    const leadPages = siteSettings?.lead_pages;
    if (leadPages?.use_external_pages && leadPages?.quote_page_url) {
      // Redirect to external landing page
      window.open(leadPages.quote_page_url, '_blank');
    } else {
      // Open internal modal
      setShowQuoteModal(true);
    }
  };

  // Handle quote form submission
  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Send to external lead management tool
      const response = await fetch(LEAD_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...quoteForm,
          form_type: 'quote',
          source: 'case_study_page',
          project_ref: project?.title || slug,
          page_url: window.location.href
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('✅ Demande envoyée ! Nous vous recontactons rapidement.');
        setShowQuoteModal(false);
        setQuoteForm({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          service: '',
          project_ref: ''
        });
      } else {
        throw new Error(result.message || 'Une erreur est survenue');
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const loadProject = async () => {
      try {
        // Load project and site settings in parallel
        const [projectRes, settingsRes] = await Promise.all([
          api.get('/cms/portfolio'),
          api.get('/cms/site-settings').catch(() => ({ data: null }))
        ]);
        
        const projects = projectRes.data || [];
        const found = projects.find(p => p.slug === slug || p.id === slug);
        if (found) {
          setProject(found);
        } else {
          setProject({
            id: slug,
            slug,
            title: "Projet de démonstration",
            client: "Client Example",
            category: "web",
            description: "Description du projet...",
            image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200",
            gallery: [],
            objectives: ["Objectif 1", "Objectif 2"],
            results: [{ metric: "+50%", label: "d'amélioration" }],
            testimonial: null
          });
        }
        
        // Store site settings for lead pages configuration
        if (settingsRes.data) {
          setSiteSettings(settingsRes.data);
        }
      } catch (error) {
        console.error('Error loading project:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProject();
  }, [slug]);

  const getCategoryInfo = (cat) => {
    switch (cat) {
      case 'graphique': return { label: 'Création Graphique', color: 'bg-pink-500' };
      case 'web': return { label: 'Création Web', color: 'bg-cyan-500' };
      case 'video': return { label: 'Création Vidéo', color: 'bg-purple-500' };
      case 'audio': return { label: 'Création Audio', color: 'bg-blue-500' };
      default: return { label: 'Projet', color: 'bg-slate-500' };
    }
  };

  const openLightbox = (item, index) => {
    setLightbox({ open: true, index });
  };

  const closeLightbox = () => {
    setLightbox({ open: false, index: 0 });
  };

  const galleryItems = project?.gallery || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="pt-32 pb-16 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Projet non trouvé</h1>
          <Link to="/site/realisations" className="text-cyan-500 hover:underline">
            Retour aux réalisations
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const categoryInfo = getCategoryInfo(project.category);
  const heroImage = project.image?.startsWith('http') ? project.image : `${process.env.REACT_APP_BACKEND_URL}${project.image}`;

  // Render category-specific content section
  const renderCategoryContent = () => {
    switch (project.category) {
      case 'graphique':
        return (
          <GraphicShowcase 
            items={galleryItems} 
            projectTitle={project.title}
            onSelect={openLightbox}
          />
        );
      
      case 'audio':
        return (
          <VinylShowcase 
            audioUrl={project.audio_url || galleryItems.find(i => i.type === 'audio')?.url}
            title={project.audio_title || project.title}
            coverImage={project.image}
            artistName={project.client || "Agence MSC"}
          />
        );
      
      case 'video':
        // Get all YouTube videos from gallery
        const youtubeVideos = galleryItems
          .filter(i => i.type === 'youtube' || i.type === 'video')
          .map(i => ({ url: i.url, caption: i.caption || i.title }));
        
        // Add main youtube_url if exists and not already in list
        const mainYoutubeUrl = project.youtube_url || project.video_url;
        if (mainYoutubeUrl && !youtubeVideos.find(v => v.url === mainYoutubeUrl)) {
          youtubeVideos.unshift({ url: mainYoutubeUrl, caption: project.title });
        }
        
        // Detect if portrait video (check video_orientation field or default to landscape)
        const isPortraitVideo = project.video_orientation === 'portrait' || project.is_portrait === true;
        
        if (youtubeVideos.length > 0) {
          return (
            <VideoShowcase 
              youtubeUrl={youtubeVideos[0]?.url}
              projectTitle={project.title}
              isPortrait={isPortraitVideo}
              videos={youtubeVideos}
            />
          );
        }
        
        // Fallback to video series gallery for non-youtube videos
        return (
          <VideoSeriesGallery 
            videos={galleryItems}
            projectTitle={project.title}
          />
        );
      
      case 'web':
        return (
          <WebsiteShowcase 
            websiteUrl={project.website_url}
            projectTitle={project.title}
            previewImage={project.image}
            screenshotDesktop={project.screenshot_desktop}
            screenshotTablet={project.screenshot_tablet}
            screenshotMobile={project.screenshot_mobile}
          />
        );
      
      default:
        // Fallback to standard gallery
        if (galleryItems.length > 0) {
          return (
            <section className="py-16">
              <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center gap-2 mb-8">
                  <ImageIcon className="w-6 h-6 text-[#00d4aa]" />
                  <h2 className="text-2xl font-bold text-slate-900">Galerie</h2>
                  <Badge variant="secondary" className="ml-2">
                    {galleryItems.filter(i => i.type === 'image' || !i.type).length} images
                    {galleryItems.filter(i => i.type === 'video').length > 0 && 
                      ` • ${galleryItems.filter(i => i.type === 'video').length} vidéos`
                    }
                  </Badge>
                </div>
                <MediaGallery items={galleryItems} onSelect={openLightbox} />
              </div>
            </section>
          );
        }
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader transparent={true} />

      {/* Hero Section */}
      <section className="relative min-h-[75vh] md:min-h-[85vh] flex items-end">
        <div className="absolute inset-0 top-0">
          <img 
            src={heroImage}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-slate-900/20" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 pb-12 md:pb-16 w-full">
          <Link 
            to="/site/realisations" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 md:mb-8 transition-colors text-sm md:text-base bg-black/20 backdrop-blur-sm px-3 py-1.5 rounded-full"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour aux projets</span>
            <span className="sm:hidden">Retour</span>
          </Link>

          <div className="flex flex-wrap items-center gap-3 mb-3">
            <Badge className={cn("md:mb-0", categoryInfo.color)}>{categoryInfo.label}</Badge>
            {project.client_logo_url && (
              <img 
                src={project.client_logo_url.startsWith('http') ? project.client_logo_url : `${process.env.REACT_APP_BACKEND_URL}${project.client_logo_url}`}
                alt={project.client}
                className="h-8 md:h-10 w-auto object-contain bg-white/90 backdrop-blur-sm rounded px-2 py-1"
              />
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-3 md:mb-4">
            {project.title}
          </h1>
          
          {project.client && (
            <p className="text-lg md:text-xl text-white/70 mb-4 md:mb-6">
              Client : <span className="text-white font-semibold">{project.client}</span>
            </p>
          )}
          
          <p className="text-base md:text-lg text-white/80 max-w-2xl">
            {project.description}
          </p>
        </div>
      </section>

      {/* Objectives & Results */}
      {(project.objectives?.length > 0 || project.results?.length > 0) && (
        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {project.objectives?.length > 0 && (
                <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                    <Target className="w-4 h-4 text-cyan-600" />
                    <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Objectifs</h2>
                  </div>
                  <ul className="space-y-2.5">
                    {project.objectives.map((obj, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <CheckCircle className="w-4 h-4 text-cyan-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-600 leading-relaxed">{obj}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {project.results?.length > 0 && (
                <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                    <TrendingUp className="w-4 h-4 text-cyan-600" />
                    <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Résultats</h2>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {project.results.map((result, idx) => {
                      const isString = typeof result === 'string';
                      const metric = isString ? result : result.metric;
                      const label = isString ? '' : result.label;
                      
                      return (
                        <div key={idx} className="bg-gradient-to-br from-cyan-50 to-slate-50 rounded-lg p-4 border border-cyan-100/50">
                          <p className="text-lg font-bold text-cyan-600 leading-tight">{metric}</p>
                          {label && <p className="text-xs text-slate-500 mt-1">{label}</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Category-Specific Content Section */}
      {renderCategoryContent()}

      {/* Testimonial Section */}
      {project.testimonial_enabled && project.testimonial && (project.testimonial.text || project.testimonial.video_url) && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4">
            <ClientTestimonial 
              testimonial={project.testimonial}
              showText={!!project.testimonial.text}
              showVideo={project.show_testimonial_video !== false && !!project.testimonial.video_url}
            />
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#06b6d4] to-[#0891b2]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Un projet similaire ?
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Discutons de vos objectifs et voyons comment nous pouvons vous aider
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-[#06b6d4] hover:bg-slate-100"
              onClick={handleQuoteAction}
            >
              <Mail className="w-5 h-5 mr-2" />
              Demander un devis
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => window.location.href = `tel:${AGENCY_CONTACT.phone.replace(/\s/g, '')}`}
            >
              <Phone className="w-5 h-5 mr-2" />
              {AGENCY_CONTACT.phone}
            </Button>
          </div>
        </div>
      </section>

      <SiteFooter />

      {/* Lightbox */}
      {lightbox.open && galleryItems.length > 0 && (
        <Lightbox 
          items={galleryItems}
          currentIndex={lightbox.index}
          onClose={closeLightbox}
          onPrev={() => setLightbox(prev => ({ ...prev, index: (prev.index - 1 + galleryItems.length) % galleryItems.length }))}
          onNext={() => setLightbox(prev => ({ ...prev, index: (prev.index + 1) % galleryItems.length }))}
        />
      )}

      {/* Quote Modal */}
      <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Demander un devis</DialogTitle>
            <p className="text-sm text-slate-500">
              Projet similaire à : {project?.title}
            </p>
          </DialogHeader>
          
          <form onSubmit={handleQuoteSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom *</Label>
                <Input 
                  id="name"
                  value={quoteForm.name}
                  onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                  required
                  placeholder="Votre nom"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Entreprise</Label>
                <Input 
                  id="company"
                  value={quoteForm.company}
                  onChange={(e) => setQuoteForm({...quoteForm, company: e.target.value})}
                  placeholder="Votre entreprise"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input 
                  id="email"
                  type="email"
                  value={quoteForm.email}
                  onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                  required
                  placeholder="votre@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone *</Label>
                <Input 
                  id="phone"
                  type="tel"
                  value={quoteForm.phone}
                  onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                  required
                  placeholder="06 12 34 56 78"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message">Votre projet</Label>
              <Textarea 
                id="message"
                value={quoteForm.message}
                onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})}
                placeholder="Décrivez brièvement votre projet..."
                rows={4}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowQuoteModal(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {submitting ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseStudyPage;
